import * as Sentry from '@sentry/browser';
import Vue from 'vue';
import { Route } from 'vue-router';
import { ActionTree, Dictionary, Getter, GetterTree, Module, MutationTree } from 'vuex';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';
import { WebLocale } from '@/enums/web-locale';
import { OfferMonetizationType, OfferPresentationType, TitleObjectType } from '@/interfaces/titles';

import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { getCollectionTypeByNewPageType } from '@/helpers/graphql-model-helper';
import { ObjectHelper } from '@/helpers/object-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import StateHelper from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getVm } from '@/helpers/vm-helper';
import { TabType } from '@/interfaces/inner-tabs';

export interface FilterValue {
	webLocale: WebLocale;
	collectionType: CollectionType;
	filterName: FilterName;
	filterValue: any;
}

export interface FilterCollection {
	age_certifications: string[]; // "technicalName" of AgeCertification
	content_types: TitleObjectType[];
	exclude_providers: string[];
	genres: string[]; // "short_code" from different genres
	exclude_genres: string[];
	event_date: Date | undefined;
	languages: string[] | null;
	min_price: number | null;
	min_runtime: number | null; // minutes
	matching_offers_only: boolean | null;
	max_price: number | null;
	max_runtime: number | null; // minutes
	monetization_types: OfferMonetizationType[];
	presentation_types: OfferPresentationType[];
	providers: string[]; // "short_name"s of different providers
	release_year_from: number | null;
	release_year_until: number | null;
	scoring_filter_types: Record<string, { min_scoring_value: number; max_scoring_value?: number }> | null;
	timeline_type: string | null;
	sort_by: TitleOrder | null;
	sort_asc: boolean | null;
	enable_provider_filter: boolean;
	inner_tab?: TabType;
	production_countries: string[];
	exclude_production_countries: string[];
	is_upcoming: boolean;
	sorting_random_seed?: number;
}

export type CountryFilters = {
	[collection in CollectionType]: FilterCollection;
};
export type FilterState = {
	[country: string]: CountryFilters;
};

export type FilterName = keyof FilterCollection;

export const getDefaultFilterCollection = (): FilterCollection => ({
	age_certifications: [], // "technicalName" of AgeCertification
	content_types: [],
	exclude_providers: [],
	genres: [], // "short_code" from different genres
	exclude_genres: [],
	event_date: undefined,
	languages: null,
	min_price: null,
	min_runtime: null,
	matching_offers_only: null,
	max_price: null,
	max_runtime: null,
	monetization_types: [],
	presentation_types: [],
	providers: [], // "short_name"s of different providers
	release_year_from: null,
	release_year_until: null,
	scoring_filter_types: null,
	timeline_type: null,
	sort_by: null,
	sort_asc: null,
	enable_provider_filter: false,
	production_countries: [],
	exclude_production_countries: [],
	is_upcoming: false,
});

export const platformCollectionTypes: CollectionType[] = Object.values(CollectionType);

// will be populated in INIT_STATE
const state = () => ({} as FilterState);

type State = ReturnType<typeof state>;

const fillFilterCollection = (filterCollection?: FilterCollection): FilterCollection => ({
	...getDefaultFilterCollection(),
	...filterCollection,
});

const getDefaultCountryFilters = (): CountryFilters =>
	ObjectHelper.fromEntries(
		platformCollectionTypes.map(collectionType => [collectionType, getDefaultFilterCollection()])
	);

export const fillCountryFilters = (countryFilters?: CountryFilters): CountryFilters => {
	if (!countryFilters) {
		return getDefaultCountryFilters();
	}

	return ObjectHelper.fromEntries(
		platformCollectionTypes.map(collectionType => [
			collectionType,
			fillFilterCollection(countryFilters[collectionType]),
		])
	);
};

function getFilterState(state: any, rootState: any, type: CollectionType): FilterCollection {
	return state[rootState.language.webLocale][type];
}

/**
 * this interface is used for URL param mapping from URL to filter states.
 */
export type CollectionStateUrlParams = Dictionary<string>;
/**
 * since the url query parameters can differ from the base collection state one, we have this monster of a type here.
 */
export type CollectionStateUrlQuery = Partial<
	Record<
		Exclude<
			| keyof FilterCollection
			| 'q'
			| 'person_id'
			| 'preferred_variant'
			| 'remove_preferred_experiment'
			| 'snowplow'
			| 'content_type',
			'content_types'
		>,
		string
	>
>;

// Since all subpages of New share the filter state, we don't need getters for any of the subpages but New itself
type CollectionTypeWithoutNewSubpages = Exclude<
	CollectionType,
	CollectionType.COMING_SOON | CollectionType.LEAVING_SOON | CollectionType.PRICEDROPS
>;

// GETTERS
const getters: GetterTree<State, any> & { [key in CollectionTypeWithoutNewSubpages]: Getter<State, any> } = {
	[CollectionType.GLOBAL]: (state, _getters, rootState) => getFilterState(state, rootState, CollectionType.GLOBAL),
	[CollectionType.SEENLIST]: (state, _getters, rootState) =>
		getFilterState(state, rootState, CollectionType.SEENLIST),
	[CollectionType.POPULAR]: (state, _getters, rootState) => {
		return getFilterState(state, rootState, CollectionType.POPULAR);
	},
	[CollectionType.UPCOMING]: (state, _getters, rootState) =>
		getFilterState(state, rootState, CollectionType.UPCOMING),
	[CollectionType.NEW]: (state, _getters, rootState) => getFilterState(state, rootState, CollectionType.NEW),
	[CollectionType.SEARCH]: (state, _getters, rootState) => getFilterState(state, rootState, CollectionType.SEARCH),
	[CollectionType.OFFERS]: (state, _getters, rootState) => getFilterState(state, rootState, CollectionType.OFFERS),
	[CollectionType.WATCHLIST]: (state, _getters, rootState) =>
		getFilterState(state, rootState, CollectionType.WATCHLIST),
	[CollectionType.TITLELIST]: (state, _getters, rootState) =>
		getFilterState(state, rootState, CollectionType.TITLELIST),
	[CollectionType.SPORTS]: (state, _getters, rootState) => getFilterState(state, rootState, CollectionType.SPORTS),

	// Not really needed but just return the popular page filters
	[CollectionType.EDITORIAL]: (state, _getters, rootState) =>
		getFilterState(state, rootState, CollectionType.EDITORIAL),

	filters: (state, getters, rootState, rootGetters) => (collectionType: CollectionType) => {
		const webLocale = rootState.language.webLocale as WebLocale;
		return state[webLocale]?.[collectionType] ?? getDefaultFilterCollection();
	},

	/**
	 * Returns the whole current filter state for the current collection type
	 */
	currentFilters(state, getters, rootState, rootGetters): FilterCollection {
		const webLocale: string = rootState.language.webLocale;

		if (!state[webLocale]) {
			console.error('[filter.store] getter/currentFilters state not initialized for webLocale: ', webLocale);
		}

		const collectionType = rootGetters['routing/lastActiveCollectionType'] as CollectionType;

		// TODO Remove after fix https://sentry.justwatch.com/organizations/justwatch/issues/25003
		// This value should never be undefined, but it mysteriously is!
		if (!state[webLocale][collectionType]) {
			captureMessageForSentry(
				'Filter collection udefined',
				{
					where: '[filter.store.ts] currentFilters',
					webLocale,
					collectionType,
					filterState: JSON.stringify(state),
					collection: JSON.stringify(state[webLocale][collectionType]),
				},
				Sentry.Severity.Error
			);
		}

		return state[webLocale][collectionType];
	},

	/**
	 * returns a single top level value from the current collection type filter
	 *
	 * @TODO rename to 'currentFilterValue'
	 */
	currentFilter: (state, getters) => (filterName: FilterName) => {
		return getters.currentFilters[filterName];
	},

	/**
	 * WARNING this is depracated
	 * TODO: remove after deep analisys
	 *
	 * Check if the given filter is active in any way. A value can provided to
	 * check if that specific value is currently set or part of the array of
	 * options which are active.
	 */
	isFilterActive:
		() =>
		(filterName: FilterName, value: any = undefined, currentFilters: FilterCollection): boolean => {
			console.log('WARNING: do not use this reactively. it will trigger renders when switching tabs.');
			const currentFilterValue = currentFilters[filterName];

			// when a value was passed check if that value is part of the filter
			// value’s items or when it’s no array filter value compare directly
			if (value !== undefined) {
				if (Array.isArray(currentFilterValue)) {
					// some filters are considered to be "active" when their value
					// contains no elements
					switch (filterName) {
						case 'age_certifications':
						case 'genres':
						case 'exclude_genres':
						case 'monetization_types':
						case 'presentation_types':
							if (currentFilterValue.length === 0) {
								return false;
							}
							break;
						case 'providers':
							if (currentFilterValue.length === 0) {
								return true;
							}
					}
					return (currentFilterValue as any[]).includes(value);
				}
				return currentFilterValue === value;
			}

			// no value was passed for comparison, consider anything in array values
			// as "active" or return the current filter value
			if (Array.isArray(currentFilterValue)) {
				return currentFilterValue.length > 0;
			} else {
				return currentFilterValue as boolean;
			}
		},
};

// ACTIONS
const actions: ActionTree<State, any> = {
	setRange: (
		{ commit, dispatch, rootState, rootGetters },
		{
			rangeName,
			trackingKey,
			upper,
			lower,
			selectedState,
			updateRouteState = true,
			isPremiumFilter = false,
		}: {
			rangeName: 'price' | 'release_year' | 'runtime';
			trackingKey: string;
			upper: number | null;
			lower: number | null;
			selectedState?: 1 | 0;
			updateRouteState?: boolean;
			isPremiumFilter?: boolean;
		}
	) => {
		const collectionType = rootGetters['routing/activeCollectionType'] as CollectionType;
		const filterKeys = {
			price: { lower: 'min_price', upper: 'max_price' },
			release_year: { lower: 'release_year_from', upper: 'release_year_until' },
			runtime: { lower: 'min_runtime', upper: 'max_runtime' },
		};
		const filterState = {
			[filterKeys[rangeName].lower]: lower,
			[filterKeys[rangeName].upper]: upper,
		};
		commit('SET_FILTERS', {
			webLocale: rootState.language.webLocale,
			collectionType,
			filterState,
		});
		updateRouteState && dispatch('updateRouterState');

		TrackingHelper.trackFilterToggleEvent(
			{
				label: rangeName,
				property: trackingKey,
				value: selectedState,
			},
			isPremiumFilter
		);
	},

	setSorting: (
		{ commit, dispatch, rootState, rootGetters },
		{ sortBy, sortAsc }: { sortBy: string; sortAsc: boolean }
	) => {
		const collectionType = rootGetters['routing/activeCollectionType'] as CollectionType;

		const filterState = {
			sort_by: sortBy,
			sort_asc: sortAsc,
		} as any;

		if (isRandomSort(sortBy, collectionType)) {
			filterState.sorting_random_seed = rootState.filter[rootState.language.webLocale][collectionType]
				.sorting_random_seed
				? rootState.filter[rootState.language.webLocale][collectionType].sorting_random_seed + 1
				: 1;
		}
		commit('SET_FILTERS', {
			webLocale: rootState.language.webLocale,
			collectionType: collectionType,
			filterState,
		});

		dispatch('updateRouterState');

		// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'sort_by',
			property: sortBy?.toLowerCase(),
		});
	},

	setSortingGraphql: (
		{ commit, dispatch, rootState, rootGetters },
		{ sortBy, sortAsc }: { sortBy: TitleOrder; sortAsc: boolean }
	) => {
		const collectionType = rootGetters['routing/activeCollectionType'] as CollectionType;

		const filterState: { sort_by: TitleOrder; sort_asc: boolean; sorting_random_seed?: number } = {
			sort_by: sortBy,
			sort_asc: sortAsc,
		};

		if (isRandomSort(sortBy, collectionType)) {
		}

		commit('SET_FILTERS', {
			webLocale: rootState.language.webLocale,
		});

		dispatch('updateRouterState');

		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'sort_by',
			property: sortBy,
		});
	},

	resetFilter: (
		{ dispatch },
		{
			filterName,
			updateRouteState = true,
		}: { filterName: FilterName; resetAllCollections: boolean; updateRouteState?: boolean }
	) => {
		dispatch('setFilterValue', {
			filterValue: getDefaultFilterCollection()[filterName],
			filterName,
			updateRouteState,
		});
	},

	resetFilterForCollectionTypes: (
		{ dispatch },
		{ filterName, collectionTypes }: { filterName: FilterName; collectionTypes?: CollectionType[] }
	) => {
		dispatch('setFilterValueForCollectionTypes', {
			filterValue: getDefaultFilterCollection()[filterName],
			filterName,
			collectionTypes: collectionTypes ?? platformCollectionTypes,
		});
	},

	resetFilters: (
		{ commit, dispatch, rootGetters, rootState },
		{ filterNames, updateRouteState = true }: { filterNames: FilterName[]; updateRouteState: boolean }
	) => {
		filterNames.forEach((filterName: FilterName) => {
			const filterValue = getDefaultFilterCollection()[filterName];
			const activeCollectionType = rootGetters['routing/activeCollectionType'] as CollectionType;
			const webLocale = rootState.language.webLocale;
			commit('SET_FILTER_VALUE', { webLocale, collectionType: activeCollectionType, filterName, filterValue });
		});

		if (updateRouteState) {
			dispatch('updateRouterState');
		}
	},

	resetPremiumFilters: async ({ dispatch }) => {
		const premiumFilterNames: FilterName[] = [
			'min_runtime',
			'max_runtime',
			'production_countries',
			'exclude_production_countries',
			'sort_by',
			'scoring_filter_types',
		];

		const collectionTypes = [CollectionType.NEW, CollectionType.POPULAR];

		const resetFilter = async (filterName: FilterName, collectionType?: CollectionType) => {
			await dispatch('setFilterValue', {
				filterName,
				filterValue: getDefaultFilterCollection()[filterName],
				collectionType,
				updateRouteState: false,
			});
		};

		for (const collectionType of collectionTypes) {
			for (const filterName of premiumFilterNames) {
				await resetFilter(filterName, collectionType);
			}
		}
	},

	reset: (
		{ dispatch, commit, rootState, rootGetters },
		payload: { collectionTypeOverride?: CollectionType; updateRouterState?: boolean }
	) => {
		const webLocale = rootState.language.webLocale;
		let collectionType = rootGetters['routing/activeCollectionType'] as CollectionType;

		if (payload?.collectionTypeOverride) {
			collectionType = payload.collectionTypeOverride;
		}

		const filterState = getDefaultFilterCollection();
		commit('SET_FILTERS', { webLocale, collectionType, filterState });
		dispatch('updateRouterState', { collectionType });

		TrackingHelper.trackEvent('userinteraction', {
			action: 'reset',
		});
	},

	toggle: (
		{ getters, dispatch, rootState },
		{
			filterName,
			value,
			selectedState,
			trackEvent = true,
			isPremiumFilter = false,
			updateRouteState = true,
			// temporary solution until we refactor this logic
			userSelectedPackages,
		}: {
			filterName: FilterName;
			value: any;
			selectedState?: 1 | 0;
			trackEvent?: boolean;
			isPremiumFilter?: boolean;
			updateRouteState?: boolean;
			userSelectedPackages?: string[];
		}
	) => {
		const userProviders = userSelectedPackages || rootState.user.settings.providers || [];
		const oldValues = getters.currentFilter(filterName);
		let filterValue = Array.isArray(oldValues) ? oldValues.slice(0) : [];

		// in case 'all' as been toggled, ignore it
		if (value === null && oldValues.length === 0) {
			return;
		}
		switch (filterName) {
			// only can have 1 value selected at once
			case 'content_types':
			case 'presentation_types':
			case 'inner_tab':
				const oldValueIsUnset = !oldValues || oldValues?.length === 0;
				const oldValueIsDifferent = oldValues?.length === 1 && oldValues[0] !== value;
				const newValueIsValid = value !== null;

				if (oldValueIsUnset || (oldValueIsDifferent && newValueIsValid)) {
					filterValue = [value];
				} else {
					filterValue = [];
				}

				break;
			default:
				if (value === null) {
					filterValue = [];
				} else if (filterValue.includes(value)) {
					// add or remove the value from the arrays
					filterValue.splice(filterValue.indexOf(value), 1);
				} else {
					filterValue.push(value);
				}
				break;
		}
		if (filterValue.length === 0 || ObjectHelper.arrayOfStringsAreEquals(filterValue, userProviders)) {
			dispatch('resetFilter', { filterName, updateRouteState });
		} else {
			dispatch('setFilterValue', { filterName, filterValue, updateRouteState });
		}

		if (trackEvent) {
			TrackingHelper.trackFilterToggleEvent(
				{
					label: filterName,
					property: value ? value : 'all',
					value: selectedState,
				},
				isPremiumFilter
			);
		}
		if (!['content_types', 'providers'].includes(filterName)) {
			providerPageExpTrackEvent({ action: 'click', label: 'filter_toggled' });
		}
	},

	/**
	 * sets the filter and updates the state.
	 */
	setFilterValue: (
		{ dispatch, rootState, rootGetters, commit },
		{
			filterName,
			filterValue,
			collectionType,
			updateRouteState = true,
		}: {
			filterName: FilterName;
			filterValue: any;
			collectionType?: CollectionType;
			updateRouteState?: boolean;
		}
	) => {
		if (!collectionType) {
			collectionType = rootGetters['routing/activeCollectionType'] as CollectionType;
		}
		const webLocale = rootState.language.webLocale;
		commit('SET_FILTER_VALUE', { webLocale, collectionType, filterName, filterValue });
		updateRouteState && dispatch('updateRouterState');
	},

	setFilterValueForCollectionTypes: (
		{ rootState, commit },
		{
			filterName,
			filterValue,
			collectionTypes,
		}: {
			filterName: FilterName;
			filterValue: any;
			collectionTypes: CollectionType[];
		}
	) => {
		const webLocale = rootState.language.webLocale;

		collectionTypes.forEach(collectionType =>
			commit('SET_FILTER_VALUE', { webLocale, collectionType, filterName, filterValue })
		);
	},

	updateRouterState: (
		{ state, rootState, rootGetters }: { state: FilterState; rootState: any; rootGetters: any },
		payload: {
			webLocale?: WebLocale;
			collectionType?: CollectionType;
			routeQuery?: Route['query'];
		}
	) => {
		let { webLocale, collectionType, routeQuery } = {
			webLocale: rootState.language.webLocale as WebLocale,
			collectionType: rootGetters['routing/activeCollectionType'] as CollectionType,
			routeQuery: rootState.routing.activeRoute.query,
			...payload,
		};

		// watchlist always uses it's own set of filters
		if (collectionType !== 'titlelist') {
			StateHelper.updateState(
				getVm().$router,
				webLocale,
				undefined,
				getCollectionTypeByNewPageType(rootState.routing.activeRoute?.meta?.newPageType) ?? collectionType,
				state[webLocale][collectionType],
				rootGetters['constant/providersByShortName'],
				rootGetters['constant/genresByShortName'],
				routeQuery
			);
		}
	},

	/**
	 * in case a language has been changed, we might need to re-initialize the state that has been initialized from `languageStore.state.webLocale`.
	 */
	initFiltersPerLocale: ({ state, commit }, webLocale: WebLocale) => {
		commit('INIT_STATE', { webLocale, newState: fillCountryFilters(state[webLocale]) });
	},
};

// MUTATIONS
const mutations: MutationTree<State> = {
	SET_FILTERS(
		state,
		payload: { webLocale: WebLocale; collectionType: CollectionType; filterState: FilterCollection }
	) {
		const { webLocale, filterState } = payload;

		let { collectionType } = payload;

		state[webLocale][collectionType] = {
			...state[webLocale][collectionType],
			...filterState,
		};
	},
	SET_FILTER_VALUE: function (
		state,
		{
			webLocale,
			collectionType,
			filterName,
			filterValue,
		}: {
			webLocale: WebLocale;
			collectionType: CollectionType | CollectionType[];
			filterName: FilterName;
			filterValue: any;
		}
	) {
		const collectionTypes = Array.isArray(collectionType) ? collectionType : [collectionType];
		for (const collectionType of collectionTypes) {
			state[webLocale][collectionType] = {
				...state[webLocale][collectionType],
				[filterName]: filterValue,
			};
		}
	},

	INIT_STATE: function (state, { webLocale, newState }: { webLocale: WebLocale; newState: any }) {
		Vue.set(state, webLocale, newState);
	},
};

function isRandomSort(sortBy: string, collectionType: string): boolean {
	return (
		(collectionType === CollectionType.POPULAR && sortBy === TitleOrder.RANDOM) ||
		(collectionType === CollectionType.WATCHLIST && sortBy === TitleOrder.RANDOM)
	);
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<State, any>;
